/**
 * @author Harish.R <harish.r@314ecorp.com>
 * util for eligibility response
 */
import _ from 'lodash';
import {
	ELIGIBILITY_PAYER_STATUS_CODES,
	ELIGIBILITY_RESPONSE_TAG_STATUS,
	PARENT_CATEGORIES,
	SUCCESS_REQUEST_CODE,
} from 'app-constants/eligibility/index';
import { ELIGIBILITY_BENEFIT_STATUS_CODES, BENEFIT_TYPES, TIME_QUALIFIER_MAPPING } from 'app-constants/eligibility/benefit';

const getPayerMeta = (payer?: Payers.ISelectedPayer): { isArchived: boolean } => {
	return { isArchived: payer?.status === ELIGIBILITY_PAYER_STATUS_CODES.archived };
};

const canPrintResponse = (status?: string): boolean => {
	return _.isEqual(status, SUCCESS_REQUEST_CODE);
};

const getStatus = (status: string[]): string[] => {
	const statusCodes = [] as string[][];
	_.map(status, (item) => {
		if (item === 'Unknown') {
			statusCodes.push(ELIGIBILITY_BENEFIT_STATUS_CODES);
		} else {
			const keys = _.keys(_.pickBy(ELIGIBILITY_RESPONSE_TAG_STATUS, { parentCategory: item }));
			statusCodes.push(keys);
		}
	});
	return _.flatMap(statusCodes);
};

const getTitle = (key: string): string => {
	const parts = _.initial(_.split(key, ' - '));
	return parts.length > 0 ? parts.join(' - ') : '';
};

const prepareAnchorSchema = (data: EligibilityResponse.IParsedCoverage, searchText: string): EligibilityResponseSchema.IAnchorSchema[] => {
	const anchorSchema: EligibilityResponseSchema.IAnchorSchema[] = [];
	const lowerSearchText = _.toLower(searchText);
	_.forEach(data?.coverages, (serviceType, index) => {
		if (lowerSearchText && !_.includes(_.toLower(index), lowerSearchText)) {
			return;
		}
		const anchorItem: EligibilityResponseSchema.IAnchorSchema = {
			key: index,
			href: `#${index}`,
			title: getTitle(index) || 'Other Data',
		};
		const childAnchors: EligibilityResponseSchema.IAnchorSchema[] = [];
		_.forEach(serviceType, (__, benefitType) => {
			const uniqueKey = `${index}-${benefitType}`;
			const childAnchor: EligibilityResponseSchema.IAnchorSchema = {
				key: uniqueKey,
				href: `#${uniqueKey}`,
				title: _.head(_.split(benefitType, ' - ')) || '',
			};
			childAnchors.push(childAnchor);
		});
		anchorItem.children = childAnchors;
		anchorSchema.push(anchorItem);
	});
	return anchorSchema;
};

const isEligibilityError = (status?: string): boolean => {
	const { parentCategory } = _.get(ELIGIBILITY_RESPONSE_TAG_STATUS, _.toString(status), ELIGIBILITY_RESPONSE_TAG_STATUS.unknown);
	const NON_ERROR_PARENT_CATEGORIES = [PARENT_CATEGORIES.active, PARENT_CATEGORIES.inactive, PARENT_CATEGORIES.unknown];
	return !_.includes(NON_ERROR_PARENT_CATEGORIES, parentCategory);
};

const getFirstElementByClassName = (className: string): HTMLElement => {
	return _.first(document.getElementsByClassName(className)) as HTMLElement;
};

const TOTAL_AMOUNT_SUFFIX = [
	TIME_QUALIFIER_MAPPING[21].display,
	TIME_QUALIFIER_MAPPING[22].display,
	TIME_QUALIFIER_MAPPING[23].display,
	TIME_QUALIFIER_MAPPING[25].display,
	TIME_QUALIFIER_MAPPING[32].display,
	TIME_QUALIFIER_MAPPING[34].display,
];

const REMAINING_AMOUNT_SUFFIX = [TIME_QUALIFIER_MAPPING[29].display, TIME_QUALIFIER_MAPPING[33].display];

const YEAR_TO_DATE_AMOUNT_SUFFIX = [TIME_QUALIFIER_MAPPING[24].display];

const DEDUCTIBLE_OR_OUT_OF_POCKET_BENEFIT_TYPES_CODES = [BENEFIT_TYPES.C.code, BENEFIT_TYPES.G.code];

// get amount count for items with suffix codes
const getAmountCount = (items: EligibilityResponse.IBenefitItem[], suffixCodes: string[], checkNoQualifier = false): number =>
	_.size(
		_.filter(items, (item) =>
			_.some(item?.amount, (amt) => {
				if (checkNoQualifier) {
					const amountArray = _.split(amt.amountDetails, ' ');
					if (_.size(amountArray) === 2) {
						return true;
					}
				}
				return _.some(suffixCodes, (suffix) => _.endsWith(amt.amountDetails, suffix));
			}),
		),
	);

// check if there is max only one total amount, ytd amount and remaining amount
const areAmountsMergeable = (items: EligibilityResponse.IBenefitItem[]): boolean => {
	const totalCount = getAmountCount(items, TOTAL_AMOUNT_SUFFIX, true);
	const ytdCount = getAmountCount(items, YEAR_TO_DATE_AMOUNT_SUFFIX);
	const remainingCount = getAmountCount(items, REMAINING_AMOUNT_SUFFIX);
	return totalCount <= 1 && ytdCount <= 1 && remainingCount <= 1;
};

// check if values in allItems are not different
const areAllItemsCompatible = (itemsToCheck: EligibilityResponse.IBenefitItem[]) => {
	// flat all items
	const allItems = _.flatMap(itemsToCheck, (item) => item?.allItems);
	const groupedByLabel = _.groupBy(allItems, (item) => item?.label);
	const allItemsArray = _.mapValues(groupedByLabel, (items) => _.uniq(_.compact(_.map(items, (item) => item?.value))));

	/* Check if any item has more than one unique value. If yes, then items are not compatible.
	 If some item is present only once but although items to check is more than 1, we should merge them.*/
	return !_.some(allItemsArray, (values) => _.size(values) > 1);
};

// merge items if they are compatible, and amount can be merged
const groupItems = (items: EligibilityResponse.IBenefitItem[]): EligibilityResponse.IBenefitItem[] => {
	const canMergeItems = areAllItemsCompatible(items) && areAmountsMergeable(items);
	if (canMergeItems) {
		const allNotes = _.flatMap(items, (item) => item?.notes || []);
		const groupedNotes = _.groupBy(allNotes, (note) => note?.label);
		const uniqNotes = _.map(_.values(groupedNotes), (notes) => ({
			...notes[0],
			value: _.uniq(_.flatMap(notes, (note) => note?.value as string[])),
		}));
		// all note labels across all items
		const mergedItem = {
			...items[0],
			notes: uniqNotes,
			amount: _.flatMap(items, (item) => item?.amount || []),
			allItems: _.uniqBy(
				_.flatMap(items, (item) => item?.allItems || []),
				(item) => item.label,
			),
			additionalReferences: _.flatMap(items, (item) => item?.additionalReferences || []),
		};
		return [mergedItem];
	}

	// if can't be merged, return original items
	return items;
};

// function to handle anchor change and scroll behavior
const handleAnchorChange = (): void => {
	setTimeout(() => {
		const wrapper = document?.querySelector('.ant-anchor-wrapper');
		const activeLink = document?.querySelector('.ant-anchor-link-title-active');

		if (wrapper && activeLink) {
			const wrapperRect = wrapper?.getBoundingClientRect();
			const linkRect = activeLink?.getBoundingClientRect();

			const isAbove = linkRect?.top < wrapperRect?.top;
			const isBelow = linkRect?.bottom > wrapperRect?.bottom;

			if (isAbove || isBelow) {
				activeLink?.scrollIntoView({
					behavior: 'smooth',
					block: 'center',
					inline: 'nearest',
				});
			}
		}
	}, 100);
};

export {
	REMAINING_AMOUNT_SUFFIX,
	DEDUCTIBLE_OR_OUT_OF_POCKET_BENEFIT_TYPES_CODES,
	groupItems,
	getPayerMeta,
	canPrintResponse,
	getStatus,
	isEligibilityError,
	getFirstElementByClassName,
	prepareAnchorSchema,
	handleAnchorChange,
};
